import { useState, useEffect, useCallback } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { router } from '@inertiajs/react';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from './ai-elements/conversation';
import {
  Message,
  MessageContent,
  MessageResponse,
  MessageActions,
  MessageAction,
} from './ai-elements/message';
import {
  PromptInput,
  PromptInputBody,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
  type PromptInputMessage,
} from './ai-elements/prompt-input';
import { Loader } from './ai-elements/loader';
import { CopyIcon, RefreshCcwIcon, MessageCircleIcon, CheckIcon, PencilIcon, XIcon } from 'lucide-react';
import { Button } from './ui/button';

interface ChatInterfaceProps {
  chatId?: string;
  initialMessages?: any[];
  chatName?: string;
}

const getCsrfToken = () => {
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  return token || '';
};

const updateChatName = async (chatId: string, name: string): Promise<{ name: string; display_name: string }> => {
  const response = await fetch(`/api/chats/${chatId}/name`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    throw new Error('Failed to update chat name');
  }

  return response.json();
};

export default function ChatInterface({ chatId, initialMessages = [], chatName }: ChatInterfaceProps) {
  const [input, setInput] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastUserMessage, setLastUserMessage] = useState<string>('');
  const [chatHeight, setChatHeight] = useState<string>('650px');
  const [isStreamingNewChat, setIsStreamingNewChat] = useState<boolean>(false);
  
  // Chat name state
  const [currentChatName, setCurrentChatName] = useState<string>(chatName || '');
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [editNameValue, setEditNameValue] = useState<string>('');

  const { messages, sendMessage, status, setMessages, error, clearError, regenerate } = useChat({
    id: chatId,
    transport: new DefaultChatTransport({
      api: '/api/chat',
      headers: {
        'X-CSRF-Token': getCsrfToken(),
      },
      prepareSendMessagesRequest: ({ messages: uiMessages }) => ({
        body: {
          id: chatId,
          messages: uiMessages
        },
      }),
    }),
    onError: (error) => {
      console.error('Chat error:', error);
    },
    onFinish: () => {
      setRetryCount(0);
      setIsRetrying(false);
    },
  });

  const handleRetry = useCallback(async () => {
    if (retryCount >= 3) {
      return; // Max retries reached
    }

    setIsRetrying(true);
    clearError?.();
    setRetryCount(prev => prev + 1);

    try {
      await sendMessage({ text: lastUserMessage });
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      setIsRetrying(false);
    }
  }, [retryCount, clearError, sendMessage, lastUserMessage]);

  useEffect(() => {
    if (initialMessages.length > 0) {
      setMessages(initialMessages);
    }
  }, [initialMessages, setMessages]);

  // Update currentChatName when chatName prop changes
  useEffect(() => {
    if (chatName) {
      setCurrentChatName(chatName);
    }
  }, [chatName]);

  // Handle chat name editing
  const handleStartEditName = () => {
    setEditNameValue(currentChatName || '');
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditNameValue('');
  };

  const handleSaveName = async () => {
    if (!chatId) return;
    
    try {
      const data = await updateChatName(chatId, editNameValue);
      setCurrentChatName(data.display_name);
      setIsEditingName(false);
      setEditNameValue('');
    } catch (error) {
      console.error('Failed to update chat name:', error);
    }
  };

  useEffect(() => {
    const calculateChatHeight = () => {
      const header = document.querySelector('header');
      const promptInput = document.querySelector('[data-prompt-input]');
      const main = document.querySelector('main');

      if (header && promptInput && main) {
        const headerHeight = header.offsetHeight;
        const promptInputHeight = (promptInput as HTMLElement).offsetHeight;
        const mainPadding = parseFloat(getComputedStyle(main).paddingTop) + parseFloat(getComputedStyle(main).paddingBottom);
        const availableHeight = window.innerHeight - headerHeight - promptInputHeight - mainPadding - 60;

        const calculatedHeight = Math.min(availableHeight, 3000);

        setChatHeight(`${calculatedHeight}px`);
      }
    };

    calculateChatHeight();
    window.addEventListener('resize', calculateChatHeight);

    return () => {
      window.removeEventListener('resize', calculateChatHeight);
    };
  }, []);

  const handleSubmit = async (message: PromptInputMessage) => {
    const hasText = Boolean(message.text);

    if (!hasText) {
      return;
    }

    setLastUserMessage(message.text || '');
    setInput('');
    setRetryCount(0); // Reset retry count on new message

    // If no chatId, create new chat and stream response on current page, then redirect
    if (!chatId) {
      // Build the UI message format
      const uiMessage = {
        role: 'user',
        parts: [{ type: 'text', text: message.text }]
      };

      // Add user message immediately to display
      const tempUserMsg = {
        id: `user-${Date.now()}`,
        role: 'user' as const,
        parts: [{ type: 'text' as const, text: message.text }]
      };

      // Create assistant message placeholder for streaming
      const tempAssistantMsg = {
        id: `asst-${Date.now()}`,
        role: 'assistant' as const,
        parts: [{ type: 'text' as const, text: '' }]
      };

      // Set initial messages with user message and empty assistant message
      setMessages([tempUserMsg, tempAssistantMsg]);
      setIsStreamingNewChat(true);

      try {
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
          body: JSON.stringify({
            messages: [uiMessage],
            create_new_chat: true
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to create chat: ${response.status} - ${errorText}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let done = false;
        let chatIdFromStream: number | null = null;
        let currentAssistantText = '';

        // Parse AI SDK stream format and update messages in real-time
        while (!done) {
          const { value, done: doneReading } = await reader.read();
          done = doneReading;

          if (value) {
            const chunk = decoder.decode(value, { stream: true });

            // Parse SSE data lines
            const lines = chunk.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);

                // Skip [DONE] marker
                if (data === '[DONE]') continue;

                try {
                  const parsed = JSON.parse(data);

                  // Look for chatId in finish message
                  if (parsed.type === 'finish' && parsed.chatId) {
                    chatIdFromStream = parsed.chatId;
                  }

                  // Handle text-delta for streaming assistant response
                  if (parsed.type === 'text-delta' && parsed.delta) {
                    currentAssistantText += parsed.delta;
                    // Update assistant message with streaming text
                    setMessages(prev => prev.map((msg, i) =>
                      i === 1 ? { ...msg, parts: [{ type: 'text', text: currentAssistantText }] } : msg
                    ));
                  }
                } catch (e) {
                  // Not JSON or parse error, ignore
                }
              }
            }
          }
        }

        // Wait a short pause before redirecting
        await new Promise(resolve => setTimeout(resolve, 500));

        if (chatIdFromStream) {
          router.visit(`/chats/${chatIdFromStream}`);
        } else {
          throw new Error('chatId not found in stream');
        }

      } catch (err) {
        console.error('Failed to create/stream chat:', err);
      } finally {
        setIsStreamingNewChat(false);
      }

      return;
    }

    // Existing chat - stream normally
    sendMessage({ text: message.text || '' });
  };

  return (
    <div className="max-w-3xl mx-auto overflow-hidden  ">
      {/* Chat Title Bar */}
      {chatId && (
        <div className="flex items-center gap-2 mb-4 px-2 py-2 bg-muted/30 rounded-lg">
          <MessageCircleIcon className="size-5 text-muted-foreground" />
          {isEditingName ? (
            <div className="flex items-center gap-1 flex-1">
              <input
                type="text"
                value={editNameValue}
                onChange={(e) => setEditNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') handleCancelEditName();
                }}
                className="flex-1 px-2 py-1 text-lg font-semibold bg-background border rounded focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                autoFocus
                placeholder="Chat name..."
              />
              <Button size="icon" variant="ghost" onClick={handleSaveName} title="Save">
                <CheckIcon className="size-3" />
              </Button>
              <Button size="icon" variant="ghost" onClick={handleCancelEditName} title="Cancel">
                <XIcon className="size-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-1 group cursor-pointer" onClick={handleStartEditName}>
              <span className="text-lg font-semibold truncate">{currentChatName || 'Untitled Chat'}</span>
              <PencilIcon className="size-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          )}
        </div>
      )}
      <div id="chat-height-box" className="rounded-lg border" style={{ height: chatHeight, maxHeight: 3000 }}>
        <div className="flex flex-col h-full">
          <Conversation >
            <ConversationContent>
              {messages.map((message) => (
                <div key={message.id}>
                  {message.parts.map((part, i) => {
                    switch (part.type) {
                      case 'text':
                        return (
                          <Message key={`${message.id}-${i}`} from={message.role}>
                            <MessageContent>
                              <MessageResponse>
                                {part.text}
                              </MessageResponse>
                            </MessageContent>
                            {message.role === 'assistant' && i === message.parts.length - 1 && (
                              <MessageActions>
                                <MessageAction
                                  onClick={() => regenerate()}
                                  label="Retry"
                                >
                                  <RefreshCcwIcon className="size-3" />
                                </MessageAction>
                                <MessageAction
                                  onClick={() =>
                                    navigator.clipboard.writeText(part.text)
                                  }
                                  label="Copy"
                                >
                                  <CopyIcon className="size-3" />
                                </MessageAction>
                              </MessageActions>
                            )}
                          </Message>
                        );
                      default:
                        return null;
                    }
                  })}
                </div>
              ))}
              {status === 'submitted' && <Loader />}
            </ConversationContent>
            <ConversationScrollButton />
          </Conversation>
         </div>
      </div>
      <PromptInput onSubmit={handleSubmit} className="mt-4" data-prompt-input>
        <PromptInputBody>
          <PromptInputTextarea
            onChange={(e) => setInput(e.target.value)}
            value={input}
          />
        </PromptInputBody>
        <PromptInputFooter>
          <PromptInputSubmit disabled={!input && !status} status={status} size='icon-xs' />
        </PromptInputFooter>
      </PromptInput>
    </div>
  );
}
